/**
 * Preview Studio Panel (Right Side of Voice Studio)
 *
 * Features:
 * - Voice header with metadata
 * - Waveform audio player
 * - Greeting text editor
 * - Voice settings sliders (stability, similarity, etc.)
 * - Actions (preview, generate, select)
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Play,
  Pause,
  Square,
  Wand2,
  Check,
  Loader2,
  AlertCircle,
  Volume2,
  Settings
} from 'lucide-react';
import { type PresetVoice } from '@/lib/elevenlabs-voices';
import { supabase } from '@/utils/supabase';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { createComponentLogger } from '@/utils/logger';

interface PreviewStudioPanelProps {
  userId: string;
  userProfile: {
    firstName?: string;
    lastName?: string;
  };
  selectedVoice: PresetVoice | null;
  playingVoiceId: string | null;
  currentAudioUrl?: string | null;
  currentGreeting?: string | null;
  onPlayingChange: (voiceId: string | null) => void;
  onVoiceConfigured?: () => void;
}

const log = createComponentLogger('PreviewStudioPanel', 'voice-studio');

export function PreviewStudioPanel({
  userId,
  userProfile,
  selectedVoice,
  playingVoiceId,
  currentAudioUrl,
  currentGreeting,
  onPlayingChange,
  onVoiceConfigured
}: PreviewStudioPanelProps) {
  const { toast } = useToast();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Greeting text
  const [greetingText, setGreetingText] = useState('');

  // Voice settings
  const [speed, setSpeed] = useState(1.0);  // Speaking speed: 0.8-1.2 (1.0 = normal)

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Initialize greeting text
  useEffect(() => {
    if (currentGreeting) {
      setGreetingText(currentGreeting);
    } else {
      // Default greeting
      const firstName = userProfile.firstName || '';
      const lastName = userProfile.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();

      if (fullName) {
        setGreetingText(`Thank you for calling ${fullName}. Please hold while we connect you.`);
      } else {
        setGreetingText('Thank you for calling. Please hold while we connect you.');
      }
    }
  }, [currentGreeting, userProfile]);

  // Clear generated audio when voice selection changes
  useEffect(() => {
    // Reset the generated audio URL when a new voice is selected
    // This ensures the preview plays the correct voice
    setGeneratedAudioUrl(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    // Also reset the audio element if it exists
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Notify parent that playback stopped
    onPlayingChange(null);
  }, [selectedVoice?.id, onPlayingChange]); // Only run when voice ID changes

  // Audio player setup
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
return;
}

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      onPlayingChange(null);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onPlayingChange]);

  // Handle preview generation
  const handleGeneratePreview = async () => {
    if (!selectedVoice) {
      toast({
        title: 'No Voice Selected',
        description: 'Please select a voice from the library first.',
        variant: 'destructive'
      });
      return;
    }

    if (!greetingText.trim()) {
      toast({
        title: 'No Greeting Text',
        description: 'Please enter greeting text to preview.',
        variant: 'destructive'
      });
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const { data, error: apiError } = await supabase.functions.invoke('elevenlabs-generate-greeting', {
        body: {
          userId,
          voiceId: selectedVoice.elevenLabsId,
          greetingText,
          isPreview: true,
          speed  // Speaking speed (0.8-1.2)
        }
      });

      if (apiError) {
throw apiError;
}

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to generate preview');
      }

      setGeneratedAudioUrl(data.audioUrl);

      // Auto-play (wait for audio to load before playing)
      if (audioRef.current) {
        const audio = audioRef.current;
        audio.src = data.audioUrl;
        audio.load(); // Explicitly trigger load

        // Handle load errors
        audio.onerror = () => {
          log.error('Audio load error', { audioUrl: data.audioUrl });
          toast({
            title: 'Audio Load Error',
            description: 'Failed to load audio preview. Please try again.',
            variant: 'destructive'
          });
          audio.onerror = null;
          audio.oncanplaythrough = null;
        };

        // Wait for audio to be ready before playing
        audio.oncanplaythrough = () => {
          audio.play()
            .then(() => {
              setIsPlaying(true);
              onPlayingChange(selectedVoice.id);
            })
            .catch((err) => {
              log.warn('Autoplay blocked by browser', {}, err);
              // User can manually click play button if autoplay is blocked
              toast({
                title: 'Preview Ready',
                description: 'Click the play button to listen to your preview.'
              });
            });

          // Clean up event listeners
          audio.oncanplaythrough = null;
          audio.onerror = null;
        };
      }

      toast({
        title: 'Preview Generated',
        description: `Cost: $${data.cost || '0.003'} (${data.characters || greetingText.length} characters)`
      });

    } catch (err: any) {
      log.error('Preview generation failed', { userId, voiceId: selectedVoice.elevenLabsId }, err);
      const errorMessage = err.message || 'Failed to generate preview';
      setError(errorMessage);
      toast({
        title: 'Preview Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle voice selection and save to database
  const handleSaveVoiceConfiguration = async () => {
    if (!selectedVoice) {
      toast({
        title: 'No Voice Selected',
        description: 'Please select a voice from the library first.',
        variant: 'destructive'
      });
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      log.info('Saving voice configuration', {
        userId,
        voiceId: selectedVoice.elevenLabsId,
        voiceName: selectedVoice.name,
        hasAudio: !!generatedAudioUrl,
        hasGreeting: !!greetingText
      });

      // Map voice category to database voice_source format
      // Database constraint: voice_source IN ('preset', 'cloned', 'designed')
      // - 'cloned' or 'professional' → 'cloned' (instant/professional voice clones)
      // - 'generated' → 'designed' (voice design)
      // - 'premade' or 'high_quality' → 'preset' (library voices)
      let voiceSourceDB: 'preset' | 'cloned' | 'designed' = 'preset';
      if (selectedVoice.category === 'cloned' || selectedVoice.category === 'professional') {
        voiceSourceDB = 'cloned';
      } else if (selectedVoice.category === 'generated') {
        voiceSourceDB = 'designed';
      }

      // Generate production audio if not already generated
      let audioUrlToSave = generatedAudioUrl;

      if (!audioUrlToSave && greetingText?.trim()) {
        log.info('Generating production audio before saving', {
          voiceId: selectedVoice.elevenLabsId,
          greetingLength: greetingText.length
        });

        // Generate production audio (not preview)
        const { data, error: apiError } = await supabase.functions.invoke('elevenlabs-generate-greeting', {
          body: {
            userId,
            voiceId: selectedVoice.elevenLabsId,
            greetingText,
            isPreview: false,  // IMPORTANT: Production audio, not preview
            speed  // Speaking speed (0.8-1.2)
          }
        });

        if (apiError) {
          log.error('Failed to generate production audio', { userId }, apiError);
          throw apiError;
        }

        if (!data?.success) {
          if (data?.rateLimitExceeded) {
            setError(data.error);
            toast({
              title: 'Rate Limit Exceeded',
              description: data.error,
              variant: 'destructive'
            });
            return;
          }
          throw new Error(data?.error || 'Failed to generate production audio');
        }

        audioUrlToSave = data.audioUrl;

        log.info('Production audio generated successfully', {
          audioUrl: audioUrlToSave,
          cost: data.cost,
          characters: data.characters
        });

        toast({
          title: 'Audio Generated',
          description: `Production greeting generated. Cost: $${data.cost || '0.003'}`
        });
      }

      // Update user profile with voice configuration
      const { error: updateError } = await supabase
        .from('users')
        .update({
          elevenlabs_voice_id: selectedVoice.elevenLabsId,
          voice_source: voiceSourceDB,
          voice_greeting_text: greetingText || null,
          voice_greeting_audio_url: audioUrlToSave || null,
          voice_configured_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select();

      if (updateError) {
        log.error('Failed to save voice configuration', { userId }, updateError);
        throw updateError;
      }

      log.info('Voice configuration saved successfully', {
        userId,
        voiceId: selectedVoice.elevenLabsId
      });

      toast({
        title: 'Voice Settings Updated',
        description: `${selectedVoice.name} has been configured as your greeting voice.`
      });

      // Trigger parent reload
      onVoiceConfigured?.();

    } catch (err: any) {
      log.error('Failed to save voice configuration', { userId }, err);
      const errorMessage = err.message || 'Failed to save voice configuration';
      setError(errorMessage);
      toast({
        title: 'Save Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle play/pause toggle
  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio || !audio.src) {
return;
}

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      onPlayingChange(null);
    } else {
      audio.play()
        .then(() => {
          setIsPlaying(true);
          if (selectedVoice) {
            onPlayingChange(selectedVoice.id);
          }
        })
        .catch((err) => {
          log.error('Playback error', {}, err);
          toast({
            title: 'Playback Error',
            description: 'Failed to play audio. Please try again.',
            variant: 'destructive'
          });
        });
    }
  };

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate cost
  const estimatedCost = (greetingText.length * 0.0003).toFixed(4);

  // Empty state
  if (!selectedVoice) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center space-y-4 max-w-md">
          <div className="p-4 bg-muted/30 rounded-full inline-block">
            <Wand2 className="h-12 w-12 text-muted-foreground" />
          </div>
          <div>
            <h4 className="font-semibold text-lg mb-2">Select a Voice to Begin</h4>
            <p className="text-sm text-muted-foreground">
              Choose a voice from the library on the left to preview and customize your AI greeting.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Voice Header */}
      <div className="p-6 border-b bg-background/50 backdrop-blur">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-xl">{selectedVoice.name}</h3>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Badge variant={selectedVoice.source === 'user' ? 'default' : 'secondary'}>
                {selectedVoice.source === 'user' ? 'Your Voice' : 'Library Voice'}
              </Badge>
              {selectedVoice.category && (
                <Badge variant="outline">{selectedVoice.category}</Badge>
              )}
              {selectedVoice.featured && (
                <Badge variant="outline" className="bg-yellow-50 border-yellow-200 text-yellow-700">
                  ⭐ Featured
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-4 text-sm text-muted-foreground">
          {selectedVoice.gender && <span>👤 {selectedVoice.gender}</span>}
          {selectedVoice.accent && <span>🌍 {selectedVoice.accent}</span>}
          {selectedVoice.age && <span>🎂 {selectedVoice.age}</span>}
        </div>

        <p className="text-sm mt-3">{selectedVoice.description}</p>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Audio Player */}
        {(generatedAudioUrl || selectedVoice.previewUrl) && (
          <Card>
            <CardContent className="pt-6">
              <Label className="flex items-center gap-2 mb-3">
                <Volume2 className="h-4 w-4" />
                Audio Preview
              </Label>

              {/* Hidden audio element */}
              <audio
                key={selectedVoice.id + (generatedAudioUrl ? '-generated' : '')}
                ref={audioRef}
                src={generatedAudioUrl || selectedVoice.previewUrl || ''}
                preload="metadata"
              />

              {/* Waveform placeholder (simplified version) */}
              <div className="h-24 bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-lg p-4 mb-3">
                <div className="flex items-center justify-center h-full gap-1">
                  {[...Array(30)].map((_, i) => (
                    <div
                      key={i}
                      className={cn(
                        "w-1 bg-primary/50 rounded-full transition-all",
                        isPlaying && "animate-pulse"
                      )}
                      style={{
                        height: `${Math.random() * 100}%`,
                        animationDelay: `${i * 50}ms`
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Playback Controls */}
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={togglePlayback}
                  disabled={!audioRef.current?.src}
                >
                  {isPlaying ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>

                <div className="flex-1">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Greeting Editor */}
        <Card>
          <CardContent className="pt-6">
            <Label htmlFor="greeting-text" className="mb-2 block">
              Greeting Text
            </Label>
            <Textarea
              id="greeting-text"
              value={greetingText}
              onChange={(e) => setGreetingText(e.target.value)}
              placeholder="Enter your custom greeting message..."
              className="min-h-[100px] resize-none"
              maxLength={200}
            />
            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>{greetingText.length} / 200 characters</span>
              <span>Est. cost: ${estimatedCost}</span>
            </div>
          </CardContent>
        </Card>

        {/* Voice Settings */}
        <Card>
          <CardContent className="pt-6">
            <Label className="flex items-center gap-2 mb-4">
              <Settings className="h-4 w-4" />
              Voice Settings
            </Label>

            <div className="space-y-4">
              {/* Speaking Speed */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-sm">Speaking Speed</Label>
                  <span className="text-sm text-muted-foreground">{speed.toFixed(1)}x</span>
                </div>
                <Slider
                  value={[speed * 100]}
                  onValueChange={(val) => setSpeed(val[0] / 100)}
                  min={80}
                  max={120}
                  step={5}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Slower (0.8x)</span>
                  <span>Normal (1.0x)</span>
                  <span>Faster (1.2x)</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      {/* Actions Footer */}
      <div className="border-t p-6 bg-background/50 backdrop-blur">
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleGeneratePreview}
            disabled={isGenerating || !greetingText.trim()}
            className="flex-1"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Preview This Text
              </>
            )}
          </Button>

          <Button
            type="button"
            onClick={handleSaveVoiceConfiguration}
            disabled={isSaving}
            className="flex-1"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Select This Voice
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
